import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useReducer, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { Chip, toQuery } from "../../helpers/general";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import PacksTable from "./PacksTable";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

const PAGE_SIZE = 10;

export default function Packs() {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const searchQuery = useDebounce(query, 400);

  // Search or place change goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedPlaceId]);

  const { data, isLoading, isPreviousData } = useQuery(
    ["getAllPacks", currentPage, searchQuery, selectedPlaceId],
    () =>
      apiService.MakeGetRequest(
        `packs${toQuery({
          page: currentPage,
          limit: PAGE_SIZE,
          q: searchQuery,
          ...(!isAdmin && selectedPlaceId ? { place: selectedPlaceId } : {}),
        })}`,
        token,
      ),
    { keepPreviousData: true, enabled: Boolean(token) },
  );

  const deletePack = useMutation((id: string) =>
    apiService.MakeDeleteRequest(
      isAdmin ? `packs/${id}` : `packs/${id}?placeId=${selectedPlaceId}`,
      token,
    ),
  );

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  const handleBulkDelete = async () => {
    const ids = selected.ids.filter(Boolean) as string[];
    const results = await Promise.allSettled(
      ids.map((id) => deletePack.mutateAsync(id)),
    );

    // Only the packs that were really deleted leave the selection
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        dispatch({ type: DELETE_ACTIONS.REMOVE, id: ids[i] });
      }
    });

    if (results.some((r) => r.status === "rejected")) {
      toastError(general.fr.message.operationFailed);
    } else {
      toastSuccess(general.fr.message.packDeleted);
    }
    queryClient.invalidateQueries("getAllPacks");
  };

  const total = data?.meta?.totalItems ?? 0;

  return (
    <section>
      {/* Title + count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Packs</h5>
          {data && (
            <Chip tone="warning">
              {total} {total > 1 ? "Packs" : "Pack"}
            </Chip>
          )}
        </div>

        <Link
          to="/packs/new"
          className="btn btn-primary text-white rounded-pill px-4"
        >
          + Ajouter un pack
        </Link>
      </div>

      {/* Toolbar */}
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <div
          className="d-flex align-items-center gap-2 px-3 flex-grow-1"
          style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
        >
          <MagnifyingGlassIcon color="gray" width={18} />
          <input
            type="text"
            placeholder="Rechercher un pack"
            className="border-0 w-100 bg-transparent"
            style={{ outline: "none", fontSize: 13 }}
            value={query}
            onChange={(e) => setQuery(e.target.value.toLowerCase())}
          />
        </div>

        <button
          type="button"
          disabled={deletePack.isLoading || selectedCount === 0}
          onClick={handleBulkDelete}
          className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
        >
          {deletePack.isLoading ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            <>
              <DeleteIcon fill={colors.primary} /> Supprimer
              {selectedCount > 0 && ` (${selectedCount})`}
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading || isPreviousData}
          render={() =>
            data?.items?.length > 0 ? (
              <PacksTable
                packs={data.items as Pack[]}
                packsToBeDeleted={dispatch}
              />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        siblingCount={1}
        totalCount={data?.meta?.totalPages}
        pageSize={PAGE_SIZE}
      />
    </section>
  );
}
